import React, {useState, useEffect, useCallback, useMemo} from 'react';
import {
  TabButtons,
  NoSpecialistConsultation,
  ConsultationFilters,
  SpecialistConsultationCard,
  PatientDetailsModal,
  FeedbackModal,
  ReportViewerModal,
  ChatPanel,
} from '@/components';
import {ConsultationGroup, ConsultationType, ConsultationStatus, debounceTimer, SortOption, ConsultationAction} from '@/constants';
import {useLocation, useNavigate} from 'react-router-dom';
import {useSelector, useDispatch} from 'react-redux';
import {Routes} from '@/navigation/Routes';
import {
  specialistConsultations,
  dashboardSearchTerm,
  specialistDashboardLoadingStatus,
  specialistConsultationsNextPage,
  specialistDashboardSuccessStatus,
  specialistDashboardFailureStatus,
} from '@/services/redux/selectors';
import {
  getSpecialistConsultationsRequest,
  resetSpecialistDashboardMessage,
  setDashboardSearchTerm,
  setSpecialistCurrentConsultationTab,
} from '@/services/redux/slices';
import {Text, Icon as UIIcon} from '@/ui-kits';
import {GetSpecialistConsultationsRequestPayload, SpecialistConsultation as SpecialistConsultationType} from '@/interface';
import {LoaderCircle} from 'lucide-react';
import {useInfiniteScroll, useClickOutside, useWindowDimensions} from '@/hooks';
import {ErrorCodes, getErrorMessage, SuccessCodes} from '@/utils/getMessages';
import {positionChatPanel, resetMessage} from '@/utils/common_functions';
import {screenSize} from '@/theme';

interface FilterType {
  type: ConsultationType | null;
  status: ConsultationStatus | null;
  sort: SortOption | null;
  deadline: Date | null;
}

export const SpecialistConsultation: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const searchTerm = useSelector(dashboardSearchTerm);

  /**
   * selecctors
   */

  const [activeTab, setActiveTab] = useState<ConsultationGroup>(ConsultationGroup.Ongoing);

  // Filter and sort states

  // Get search term from Redux (managed by header on desktop, local on mobile)
  const [localSearchTerm, setLocalSearchTerm] = useState('');

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearchTerm(e.target.value);
  };

  const handleTabChange = (tab: ConsultationGroup) => {
    // reset the search term when tab is changed
    dispatch(setDashboardSearchTerm(''));
    setActiveTab(tab);
  };

  // Initialize active tab from navigation state (one-time)
  useEffect(() => {
    const tabFromState = (location.state as any)?.tab as ConsultationGroup | undefined;
    if (tabFromState) {
      setActiveTab(tabFromState);
      // Clear state so back/refresh doesn't keep re-applying
      try {
        window.history.replaceState({}, document.title);
      } catch {
        // ignore
      }
    }
  }, []);

  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  // Debounced search handler
  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      if (localSearchTerm !== searchTerm) {
        dispatch(setDashboardSearchTerm(localSearchTerm));
      }
    }, debounceTimer); // 300ms debounce

    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [localSearchTerm]);

  useEffect(() => {
    dispatch(setSpecialistCurrentConsultationTab(activeTab));
  }, [activeTab]);

  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  // Debounced search handler
  useEffect(() => {
    const debounce = setTimeout(() => {
      if (localSearchTerm !== searchTerm) {
        dispatch(setDashboardSearchTerm(localSearchTerm));
      }
    }, debounceTimer); // 300ms debounce

    return () => {
      clearTimeout(debounce);
    };
  }, [localSearchTerm, dispatch, searchTerm]);

  return (
    <div className="flex flex-col bg-gray-50 grow">
      {/* Tabs */}
      <div className="flex lg:hidden mx-1 sm:mx-4 md:mx-6 gap-2 bg-white items-center min-w-0 p-2 rounded-lg border border-ui_input_border_dark/40">
        <UIIcon type="search_blue" className="size-4 pointer-events-none" />
        <input
          type="text"
          placeholder={'Search by Patient Name or Case ID'}
          value={localSearchTerm}
          onChange={handleSearchChange}
          className="w-[280px] lg:w-[320px] xl:w-[380px] max-w-full outline-none text-sm font-FigtreeRegular placeholder:font-FigtreeRegular"
        />
      </div>
      <TabButtons activeTab={activeTab} onTabChange={handleTabChange} />

      {/* main content with loading and no consultations */}
      <Content key={activeTab} activeTab={activeTab} />
    </div>
  );
};

function Content({activeTab}: {activeTab: ConsultationGroup}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const chatPanelRef = useClickOutside<HTMLDivElement>(handleCloseModal);
  const {width} = useWindowDimensions();

  /**
   * selectors
   */
  const isloading = useSelector(specialistDashboardLoadingStatus);
  const nextPage = useSelector(specialistConsultationsNextPage);
  const consultations = useSelector(specialistConsultations);
  const specialistDashboardSuccess = useSelector(specialistDashboardSuccessStatus) as SuccessCodes;
  const specialistDashboardFailure = useSelector(specialistDashboardFailureStatus) as ErrorCodes;
  const searchTerm = useSelector(dashboardSearchTerm);

  /**
   * states
   */
  const [filterType, setFilterType] = useState<FilterType['type']>(null);
  const [filterStatus, setFilterStatus] = useState<FilterType['status']>(null);
  const [sortBy, setSortBy] = useState<FilterType['sort']>(null);
  const [deadline, setDeadline] = useState<FilterType['deadline']>(null);
  const [page, setPage] = useState(1);
  const [firstTimeUser, setFirstTimeUser] = useState<boolean>(false);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);
  const [modalType, setModalType] = useState<false | ConsultationAction>(false);

  const selectedCase = useMemo<SpecialistConsultationType | null>(() => {
    return consultations.find(consultation => consultation.id === selectedCaseId) || null;
  }, [selectedCaseId]);

  /**
   * infinite scroll setup
   */
  const observerRef = useInfiniteScroll({
    onLoadMore: () => {
      if (nextPage && nextPage > page) {
        setPage(nextPage);
      }
    },
    hasMore: nextPage !== null,
    isLoading: isloading,
  });

  /**
   * functions
   */
  const handleApplyFilters = (args: FilterType) => {
    setFilterType(args.type);
    setFilterStatus(args.status);
    setSortBy(args.sort);
    setDeadline(args.deadline);
    // reset page to 1
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilterType(null);
    setFilterStatus(null);
    setSortBy(null);
    setDeadline(null);
    // reset page to 1
    setPage(1);
  };

  const handleViewPatientDetails = (id: number) => {
    setSelectedCaseId(id);
    setModalType(ConsultationAction.View);
  };

  function handleCloseModal() {
    setSelectedCaseId(null);
    setModalType(false);
  }

  const handleViewCase = (id: number) => {
    navigate(Routes.CONSULTATION_SUMMARY.replace(':id', id.toString()));
  };

  const handleEditCase = (id: number) => {
    navigate(Routes.CONSULTATION_SUMMARY.replace(':id', id.toString()));
  };
  const handleViewFeedback = (id: number) => {
    setSelectedCaseId(id);
    setModalType(ConsultationAction.Feedback);
  };

  const handleViewReport = (id: number) => {
    setSelectedCaseId(id);
    setModalType(ConsultationAction.Report);
  };

  const handleChat = (id: number, e?: React.MouseEvent<HTMLButtonElement>) => {
    setSelectedCaseId(id);
    setModalType(ConsultationAction.Chat);

    const buttonRect = e?.currentTarget?.getBoundingClientRect();
    // only set the top position of the chat panel

    // keep the chat panel at the bottom of the page
    positionChatPanel({buttonRect, chatPanelRef});
  };

  const fetchConsultations = useCallback(() => {
    const payload: GetSpecialistConsultationsRequestPayload = {
      page: page,
      group: activeTab,
    };
    if (filterType) {
      payload.type = filterType;
    }
    if (filterStatus) {
      payload.status = filterStatus;
    }
    if (sortBy) {
      payload.sort = sortBy === SortOption.DESC ? 'desc' : 'asc';
    }
    if (searchTerm) {
      payload.search_text = searchTerm;
    }

    if (deadline) {
      payload.deadline = deadline.toISOString();
    }
    dispatch(getSpecialistConsultationsRequest(payload));
  }, [filterType, filterStatus, sortBy, deadline, activeTab, page, searchTerm]);

  /**
   * side effects
   */

  // fetch consultations
  useEffect(() => {
    fetchConsultations();
  }, [fetchConsultations]);

  // Reset page when active tab changes
  useEffect(() => {
    handleResetFilters();
  }, [activeTab]);

  useEffect(() => {
    if (specialistDashboardFailure) {
      if (specialistDashboardFailure === 'E-10091') {
        setFirstTimeUser(true);
      } else {
        setFirstTimeUser(false);
      }
    }
    return () => {
      dispatch(resetSpecialistDashboardMessage());
    };
  }, [specialistDashboardSuccess, specialistDashboardFailure]);

  // Check if there are any active filters
  const hasActiveFilters = filterType !== null || filterStatus !== null || sortBy !== null || deadline !== null || searchTerm !== '';

  if (isloading && page === 1) {
    // Show filters if there are consultations OR if filters are applied
    const shouldShowFilters = consultations.length > 0 || hasActiveFilters;

    return (
      <>
        {shouldShowFilters && (
          <ConsultationFilters
            activeTab={activeTab}
            type={filterType}
            status={filterStatus}
            sort={sortBy}
            deadline={deadline}
            onReset={handleResetFilters}
            onApply={handleApplyFilters}
            screen_type="specialist"
          />
        )}
        <div className="flex items-center justify-center px-4 sm:px-6 py-4 sm:py-6">
          <span className="text-ui_grey">Loading...</span>
          <LoaderCircle className="size-4 animate-spin text-ui_grey" />
        </div>
      </>
    );
  }

  if (firstTimeUser) {
    return (
      <div className="flex items-center justify-center px-4 sm:px-6 py-4 sm:py-6">
        <NoSpecialistConsultation tab={activeTab} />
      </div>
    );
  }

  // Hide filters only when there are no consultations AND no filters applied
  const shouldShowFilters = consultations.length > 0 || hasActiveFilters;

  return (
    <>
      {shouldShowFilters && (
        <ConsultationFilters
          activeTab={activeTab}
          type={filterType}
          status={filterStatus}
          sort={sortBy}
          deadline={deadline}
          onReset={handleResetFilters}
          onApply={handleApplyFilters}
          screen_type="specialist"
        />
      )}

      {consultations.length === 0 ? (
        <div className="flex grow items-center justify-center px-4 sm:px-6 py-4 sm:py-6">
          <Text variant="heading" className="!font-FigtreeRegular text-ui_blue text-center">
            {getErrorMessage('E-10091')}
          </Text>
        </div>
      ) : (
        <div className="space-y-4 px-4 sm:px-6 py-4 sm:py-6">
          {consultations.map((consultation: any) => (
            <SpecialistConsultationCard
              key={consultation.id}
              {...consultation}
              onViewPatientDetails={handleViewPatientDetails}
              onViewCase={handleViewCase}
              onEditCase={handleEditCase}
              onViewFeedback={handleViewFeedback}
              onViewReport={handleViewReport}
              onChat={handleChat}
            />
          ))}
          <div ref={observerRef} />
        </div>
      )}

      {/* Patient Details Modal */}
      {selectedCaseId && modalType === ConsultationAction.View && (
        <PatientDetailsModal caseId={selectedCaseId} isOpen={modalType === ConsultationAction.View} onClose={handleCloseModal} />
      )}
      {selectedCaseId && modalType === ConsultationAction.Feedback && (
        <FeedbackModal
          feedback={selectedCase?.feedback || null}
          patient_name={selectedCase?.patient_name || ''}
          patient_photo={selectedCase?.patient_photo || ''}
          isOpen={modalType === ConsultationAction.Feedback}
          case_id={selectedCase?.id.toString() || ''}
          onClose={handleCloseModal}
        />
      )}
      {selectedCaseId && modalType === ConsultationAction.Report && (
        <ReportViewerModal
          case_id={selectedCaseId}
          report_url={selectedCase?.report || ''}
          isOpen={modalType === ConsultationAction.Report}
          onClose={handleCloseModal}
        />
      )}

      <ChatPanel
        chatPanelRef={chatPanelRef}
        open={selectedCaseId !== null && selectedCase?.status !== null && modalType === ConsultationAction.Chat}
        case_status={selectedCase?.status.id || ConsultationStatus.UnderReviewByDoctor}
        reciever_profile_photo={selectedCase?.patient_photo || ''}
        reciever_name={selectedCase?.patient_name || ''}
        case_id={selectedCase?.id || 0}
        onClose={handleCloseModal}
      />
    </>
  );
}
